import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, query, where } from 'firebase/firestore';
import { db, functions } from '../../../firebase'; // Assuming functions is exported for httpsCallable
import { httpsCallable } from 'firebase/functions';
import {
    Box, Typography, CircularProgress, Paper, Chip, Stack, Grid, Avatar, Button,
    Tabs, Tab, TextField, MenuItem, FormControl, InputLabel, Select,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    List, ListItem, ListItemText, IconButton, Divider, Alert, ListItemIcon,
    Breadcrumbs, Autocomplete
} from '@mui/material';
import {
    Person as PersonIcon,
    Business as BusinessIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    LockReset as LockResetIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    DeleteOutline as DeleteOutlineIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CalendarToday as CalendarTodayIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    People as PeopleIcon,
    PersonSearch as PersonSearchIcon,
    AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useRoles from '../../../hooks/useRoles';
import { useAuth } from '../../../contexts/AuthContext';
import OrganizationAccessSection from './OrganizationAccessSection';

// Helper to format Firestore Timestamps
const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'N/A';
    return timestamp.toDate().toLocaleString();
};

// adminResetUserPassword refuses anything shorter than 8 characters, so the
// dialog has to apply the same floor or the admin sees a server error after
// the form said the password was fine.
export const MIN_RESET_PASSWORD_LENGTH = 8;

// Returns the message to show for a rejected reset, or '' when it can be sent.
export const getPasswordResetError = (newPassword, confirmNewPassword) => {
    if (newPassword !== confirmNewPassword) {
        return "Passwords do not match.";
    }
    if (newPassword.length < MIN_RESET_PASSWORD_LENGTH) {
        return `Password must be at least ${MIN_RESET_PASSWORD_LENGTH} characters long.`;
    }
    return '';
};

const UserDetail = () => {
    const { id: userId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { startRoleEmulation, userRole: currentUserRole, actualUserRole, getRoleLevel } = useAuth();

    // Determine the effective role for permission checks
    const effectiveCurrentRole = actualUserRole || currentUserRole;

    // Helper function to check if emulation is possible
    const canEmulateRole = (sourceRole, targetRole) => {
        const sourceLevel = getRoleLevel ? getRoleLevel(sourceRole) : 0;
        const targetLevel = getRoleLevel ? getRoleLevel(targetRole) : 0;
        return sourceLevel > targetLevel;
    };

    // Normalize a user's role into a plain string (role may be a string or an object)
    const getRoleString = (roleValue) => {
        if (typeof roleValue === 'string' && roleValue.trim()) return roleValue.trim();
        if (roleValue?.id) return roleValue.id;
        if (roleValue?.name) return roleValue.name.toLowerCase();
        return 'user';
    };

    // Whether the customer-restriction manager applies to a user.
    // Customer scoping is an optional, role-agnostic filter — show it for everyone
    // EXCEPT superadmin/admin, who always see everything.
    const supportsCustomerRestriction = (targetUser) => {
        const r = getRoleString(targetUser?.role);
        return r !== 'superadmin' && r !== 'admin';
    };

    // Role management
    const {
        roles,
        loading: rolesLoading,
        assignRole,
        getRoleDisplayName,
        getRoleColor,
        getActiveRoles
    } = useRoles();

    const [user, setUser] = useState(null);
    const [originalUser, setOriginalUser] = useState(null); // For reverting edits
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyMap, setCompanyMap] = useState({});
    const [allCompanies, setAllCompanies] = useState([]); // For company management modal

    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [formErrors, setFormErrors] = useState({});

    const [activeTab, setActiveTab] = useState(0);
    const [manageCompaniesOpen, setManageCompaniesOpen] = useState(false);
    const [selectedCompaniesForManagement, setSelectedCompaniesForManagement] = useState([]);

    // Customer management state (for Customer Portal role)
    const [allCustomers, setAllCustomers] = useState([]); // All customers from connected companies
    const [customerMap, setCustomerMap] = useState({}); // Map of customerId -> customer data
    const [manageCustomersOpen, setManageCustomersOpen] = useState(false);
    const [selectedCustomersForManagement, setSelectedCustomersForManagement] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    // Organization management state (for org_admin and higher roles)
    const [allOrganizations, setAllOrganizations] = useState([]); // All organizations
    const [organizationMap, setOrganizationMap] = useState({}); // Map of orgId -> org data
    const [manageOrganizationsOpen, setManageOrganizationsOpen] = useState(false);
    const [selectedOrganizationsForManagement, setSelectedOrganizationsForManagement] = useState([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordResetError, setPasswordResetError] = useState('');


    const fetchUserAndCompanyData = useCallback(async () => {
        console.log(`fetchUserAndCompanyData called for ID: ${userId}`);
        setLoading(true);
        setError(null);
        setUser(null);
        setOriginalUser(null);
        setCompanyMap({});
        setAllCompanies([]);

        try {
            // Fetch companies, organizations, and the user doc CONCURRENTLY (they are independent).
            // These were three serial round-trips gating the page spinner; Promise.all collapses
            // them to one wall-clock latency with no behavior change.
            const userDocRef = doc(db, 'users', userId);
            const [companiesSnap, orgsSnap, userDoc] = await Promise.all([
                getDocs(collection(db, 'companies')),
                getDocs(collection(db, 'organizations')),
                getDoc(userDocRef)
            ]);

            const newCompanyMap = {};
            const allCompaniesData = [];
            companiesSnap.forEach(doc => {
                const companyData = doc.data();
                if (companyData.companyID && companyData.name) {
                    newCompanyMap[companyData.companyID] = companyData.name;
                    allCompaniesData.push({ id: companyData.companyID, name: companyData.name });
                }
            });
            setCompanyMap(newCompanyMap);
            setAllCompanies(allCompaniesData);
            console.log('Company map and all companies populated:', newCompanyMap, allCompaniesData);

            // Map organizations (exclude deleted ones)
            const newOrgMap = {};
            const allOrgsData = [];
            orgsSnap.forEach(docSnap => {
                const orgData = docSnap.data();
                // Skip deleted organizations
                if (orgData.status === 'deleted') {
                    return;
                }
                const orgId = orgData.orgID || docSnap.id;
                const orgName = orgData.name || orgData.organizationName || orgId;
                newOrgMap[orgId] = orgName;
                allOrgsData.push({
                    id: orgId,
                    name: orgName,
                    logo: orgData.logo || orgData.logoUrl || null,
                    status: orgData.status || 'active'
                });
            });
            setOrganizationMap(newOrgMap);
            setAllOrganizations(allOrgsData);
            console.log('Organization map populated:', newOrgMap);

            // User document (already fetched in the Promise.all above)
            if (!userDoc.exists()) {
                console.log(`User document with ID ${userId} not found.`);
                setError('User not found');
                setLoading(false);
                return;
            }

            const userData = userDoc.data();
            console.log(`User document with ID ${userId} found:`, userData);
            setUser(userData);
            setOriginalUser(userData); // Save for potential edit cancellation
            setFormData({ // Initialize formData for editing
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                phone: userData.phone || '',
                phoneExtension: userData.phoneExtension || '',
                role: userData.role || 'user',
                status: userData.status || 'active',
            });
            // Initialize selected companies for management modal
            const connectedCompanyIds = userData.connectedCompanies?.companies || [];
            setSelectedCompaniesForManagement(allCompaniesData.filter(c => connectedCompanyIds.includes(c.id)));

            // Initialize selected organizations for management modal
            // Handle both array format (from OrganizationForm) and object format (legacy)
            const rawConnectedOrgs = userData.connectedOrganizations;
            let connectedOrgIds = [];
            if (Array.isArray(rawConnectedOrgs)) {
                // Direct array format (e.g., ['ACME', 'INTEGRATED'])
                connectedOrgIds = rawConnectedOrgs;
            } else if (rawConnectedOrgs?.organizations) {
                // Object format (e.g., { organizations: ['ACME', 'INTEGRATED'] })
                connectedOrgIds = rawConnectedOrgs.organizations;
            }
            console.log('Connected organization IDs for user:', connectedOrgIds);
            setSelectedOrganizationsForManagement(allOrgsData.filter(o =>
                connectedOrgIds.includes(o.id) || connectedOrgIds.includes(o.name)
            ));

        } catch (err) {
            console.error('Error in fetchUserAndCompanyData:', err);
            setError(`Error loading user details: ${err.message}`);
            enqueueSnackbar(`Error loading user: ${err.message}`, { variant: 'error' });
        } finally {
            console.log(`fetchUserAndCompanyData finished for ID: ${userId}. Setting loading to false.`);
            setLoading(false);
        }
    }, [userId, enqueueSnackbar]);

    useEffect(() => {
        if (userId) {
            fetchUserAndCompanyData();
        } else {
            setError("User ID is missing.");
            setLoading(false);
        }
    }, [userId, fetchUserAndCompanyData]);

    // Fetch customers from connected companies when user data changes
    const fetchCustomersFromConnectedCompanies = useCallback(async (companyIds) => {
        if (!companyIds || companyIds.length === 0) {
            setAllCustomers([]);
            setCustomerMap({});
            return;
        }

        setLoadingCustomers(true);
        try {
            const customersData = [];
            const newCustomerMap = {};

            // Fetch customers for each connected company
            for (const companyId of companyIds) {
                const customersQuery = query(
                    collection(db, 'customers'),
                    where('companyID', '==', companyId)
                );
                const customersSnap = await getDocs(customersQuery);

                customersSnap.forEach(doc => {
                    const customerData = doc.data();
                    const customer = {
                        id: doc.id,
                        customerId: customerData.customerID || doc.id,
                        name: customerData.name || customerData.customerName || 'Unnamed Customer',
                        companyId: companyId,
                        companyName: companyMap[companyId] || companyId,
                        ...customerData
                    };
                    customersData.push(customer);
                    newCustomerMap[doc.id] = customer;
                });
            }

            setAllCustomers(customersData);
            setCustomerMap(newCustomerMap);
            console.log('Fetched customers from connected companies:', customersData.length, 'customers');
        } catch (err) {
            console.error('Error fetching customers:', err);
            enqueueSnackbar(`Error loading customers: ${err.message}`, { variant: 'error' });
        } finally {
            setLoadingCustomers(false);
        }
    }, [companyMap, enqueueSnackbar]);

    // Fetch customers when user's connected companies change.
    // Candidate customers are needed for the optional customer-restriction manager,
    // which is available for any user except superadmin/admin.
    useEffect(() => {
        const connectedCompanyIds = user?.connectedCompanies?.companies || [];
        if (supportsCustomerRestriction(user) && connectedCompanyIds.length > 0) {
            fetchCustomersFromConnectedCompanies(connectedCompanyIds);
        }
    }, [user?.connectedCompanies?.companies, user?.role, fetchCustomersFromConnectedCompanies]);

    // Initialize selected customers when user data loads
    useEffect(() => {
        if (user && allCustomers.length > 0) {
            const connectedCustomerIds = user.connectedCustomers?.customers || [];
            setSelectedCustomersForManagement(
                allCustomers.filter(c => connectedCustomerIds.includes(c.id))
            );
        }
    }, [user, allCustomers]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleEditToggle = () => {
        if (editMode) {
            // If canceling edit, revert formData to original user data
            setFormData({
                firstName: originalUser.firstName || '',
                lastName: originalUser.lastName || '',
                phone: originalUser.phone || '',
                phoneExtension: originalUser.phoneExtension || '',
                role: originalUser.role || 'user',
                status: originalUser.status || 'active',
            });
            setFormErrors({});
        } else {
            // Entering edit mode, formData is already set from user state
        }
        setEditMode(!editMode);
    };

    // Emulate user handler
    const handleEmulateUser = async () => {
        if (!user) {
            enqueueSnackbar('No user data available for emulation', { variant: 'error' });
            return;
        }

        // Get user's role - ensure it's a string
        let role = 'user';
        if (typeof user.role === 'string' && user.role.trim()) {
            role = user.role.trim();
        } else if (user.role?.id) {
            role = user.role.id;
        } else if (user.role?.name) {
            role = user.role.name.toLowerCase();
        }

        // Get ALL connected companies (not just the first one)
        const connectedCompanyIds = user.connectedCompanies?.companies || [];
        const primaryCompanyId = connectedCompanyIds[0] || null;

        if (!primaryCompanyId) {
            enqueueSnackbar('This user has no connected companies. Cannot emulate.', { variant: 'warning' });
            return;
        }

        // Get company name from companyMap for primary company
        const companyName = companyMap[primaryCompanyId] || primaryCompanyId;

        // Build company data object with ALL connected companies
        const companyData = {
            companyID: primaryCompanyId,
            name: companyName,
            // Include ALL connected company IDs for proper data filtering
            connectedCompanyIds: connectedCompanyIds
        };

        // If the user is restricted to specific customers (any role), pass customer
        // data for emulation. The stored array holds BOTH the customer doc id and the
        // human customerID code per customer, so resolve against the doc-id-keyed map.
        const connectedCustomerIds = user.connectedCustomers?.customers || [];
        let customerData = null;

        if (connectedCustomerIds.length > 0) {
            // Pick the first connected customer that resolves in the customer map
            // (the array interleaves doc ids and customerID codes).
            const resolvedId = connectedCustomerIds.find(cid => customerMap[cid]) || connectedCustomerIds[0];
            const customer = customerMap[resolvedId];

            if (customer) {
                customerData = {
                    id: resolvedId,
                    name: customer.name || resolvedId,
                    customerID: customer.customerID || resolvedId,
                    companyID: customer.companyID || primaryCompanyId,
                    logo: customer.logo || customer.logoUrl || null,
                    connectedCustomers: connectedCustomerIds
                };
            } else {
                // Fallback if customer not in map
                customerData = {
                    id: resolvedId,
                    name: resolvedId,
                    customerID: resolvedId,
                    companyID: primaryCompanyId,
                    connectedCustomers: connectedCustomerIds
                };
            }
        }

        const roleString = String(role || 'user');

        // Build user data for display in emulation banner
        const userData = {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            // Thread the target user's hierarchy + org access so emulation matches their
            // real login's multi-company visibility instead of collapsing to one company (#5/#11).
            hierarchyAccess: user.hierarchyAccess || null,
            connectedOrganizations: user.connectedOrganizations || null
        };

        console.log('🎭 [UserDetail] Starting role emulation with:', {
            role: roleString,
            companyData,
            customerData,
            userData
        });

        try {
            const result = await startRoleEmulation(roleString, companyData, customerData, userData);

            if (result.success) {
                const customerInfo = customerData ? ` as ${customerData.name}` : '';
                enqueueSnackbar(`Now emulating ${user.firstName} ${user.lastName} as ${role}${customerInfo}`, { variant: 'success' });
                window.location.href = result.redirectTo;
            } else {
                // Provide more specific error message
                if (result.reason === 'same_or_higher_role') {
                    enqueueSnackbar(`Cannot emulate ${user.firstName} - their role (${role}) is at or above your current role level`, { variant: 'warning' });
                } else if (result.reason === 'no_company') {
                    enqueueSnackbar('Cannot emulate - user has no connected company', { variant: 'warning' });
                } else {
                    enqueueSnackbar('Failed to start user emulation', { variant: 'error' });
                }
            }
        } catch (error) {
            console.error('Error in handleEmulateUser:', error);
            enqueueSnackbar(`Error starting emulation: ${error.message}`, { variant: 'error' });
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        // Validate phone extension to allow only numbers
        if (name === 'phoneExtension') {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.firstName?.trim()) errors.firstName = 'First name is required.';
        if (!formData.lastName?.trim()) errors.lastName = 'Last name is required.';
        if (!formData.role) errors.role = 'Role is required.';
        if (!formData.status) errors.status = 'Status is required.';
        // Add more validation as needed (e.g., phone format)
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveChanges = async () => {
        if (!validateForm()) {
            enqueueSnackbar('Please correct the form errors.', { variant: 'warning' });
            return;
        }
        setIsSaving(true);
        try {
            const userDocRef = doc(db, 'users', userId);

            // Check if role changed - use new role assignment system
            const roleChanged = formData.role !== originalUser.role;

            if (roleChanged) {
                // Use the new role assignment system for role changes
                await assignRole(userId, formData.role, 'Role updated via admin user management');
                enqueueSnackbar(`Role updated to ${getRoleDisplayName(formData.role)}`, { variant: 'info' });
            }

            // Update other fields. Role is handled above via assignRole; status is
            // handled below via a cloud function so the Auth login state stays in sync.
            const dataToUpdate = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                phoneExtension: formData.phoneExtension,
                updatedAt: new Date(),
            };

            await updateDoc(userDocRef, dataToUpdate);

            // Status changes route through adminUpdateUserStatus so deactivating a
            // user actually disables their Firebase Auth login — the Firestore status
            // field alone is NOT enforced at sign-in. No-op when status is unchanged.
            const statusChanged = formData.status !== originalUser.status;
            if (statusChanged) {
                const updateStatusFn = httpsCallable(functions, 'adminUpdateUserStatus');
                await updateStatusFn({ userId, status: formData.status });
            }

            // Update local state with all changes including role + status
            const allUpdates = { ...dataToUpdate, role: formData.role, status: formData.status };
            setUser(prev => ({ ...prev, ...allUpdates }));
            setOriginalUser(prev => ({ ...prev, ...allUpdates }));
            setEditMode(false);
            enqueueSnackbar('User details updated successfully!', { variant: 'success' });
        } catch (err) {
            console.error("Error updating user details:", err);
            setError(`Failed to save changes: ${err.message}`);
            enqueueSnackbar(`Error saving: ${err.message}`, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleManageCompanies = () => {
        const connectedCompanyIds = user?.connectedCompanies?.companies || [];
        setSelectedCompaniesForManagement(allCompanies.filter(c => connectedCompanyIds.includes(c.id)));
        setManageCompaniesOpen(true);
    };

    const handleSaveConnectedCompanies = async () => {
        setIsSaving(true);
        try {
            const userDocRef = doc(db, 'users', userId);
            const companyIdsToSave = selectedCompaniesForManagement.map(c => c.id);
            await updateDoc(userDocRef, {
                'connectedCompanies.companies': companyIdsToSave, // Correctly targets the nested array
                // Keep hierarchy roots in lockstep with connected companies. These are
                // two sources of truth for the same company scope — the dashboard reads
                // hierarchyAccess.rootCompanyIds while shipments/address-book read
                // connectedCompanies — and letting them drift made the dashboard scope
                // to the wrong (stale) company and show zero shipments.
                'hierarchyAccess.rootCompanyIds': companyIdsToSave,
                updatedAt: new Date()
            });
            setUser(prev => ({
                ...prev,
                connectedCompanies: { ...(prev.connectedCompanies || {}), companies: companyIdsToSave }
            }));
            setOriginalUser(prev => ({
                ...prev,
                connectedCompanies: { ...(prev.connectedCompanies || {}), companies: companyIdsToSave }
            }));
            enqueueSnackbar('Connected companies updated!', { variant: 'success' });
            setManageCompaniesOpen(false);

            // Refresh candidate customers for the customer-restriction manager
            if (supportsCustomerRestriction(user)) {
                fetchCustomersFromConnectedCompanies(companyIdsToSave);
            }
        } catch (err) {
            console.error("Error updating connected companies:", err);
            enqueueSnackbar(`Error saving companies: ${err.message}`, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // Customer management handlers
    const handleManageCustomers = () => {
        const connectedCustomerIds = user?.connectedCustomers?.customers || [];
        setSelectedCustomersForManagement(allCustomers.filter(c => connectedCustomerIds.includes(c.id)));
        setManageCustomersOpen(true);
    };

    const handleSaveConnectedCustomers = async () => {
        setIsSaving(true);
        try {
            const userDocRef = doc(db, 'users', userId);
            // Persist BOTH the customer Firestore doc id AND the human customerID code
            // for each selected customer, so readers can do plain set-membership without
            // a lookup. De-duplicate the union of both forms.
            const customerIdsToSave = [...new Set(
                selectedCustomersForManagement.flatMap(c => {
                    const ids = [c.id];
                    const code = c.customerId || c.customerID;
                    if (code && code !== c.id) ids.push(code);
                    return ids;
                })
            )];
            await updateDoc(userDocRef, {
                'connectedCustomers.customers': customerIdsToSave,
                updatedAt: new Date()
            });
            setUser(prev => ({
                ...prev,
                connectedCustomers: { ...(prev.connectedCustomers || {}), customers: customerIdsToSave }
            }));
            setOriginalUser(prev => ({
                ...prev,
                connectedCustomers: { ...(prev.connectedCustomers || {}), customers: customerIdsToSave }
            }));
            enqueueSnackbar('Connected customers updated!', { variant: 'success' });
            setManageCustomersOpen(false);
        } catch (err) {
            console.error("Error updating connected customers:", err);
            enqueueSnackbar(`Error saving customers: ${err.message}`, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // Organization management handlers
    const handleManageOrganizations = () => {
        // Handle both array format and object format for connectedOrganizations
        const rawConnectedOrgs = user?.connectedOrganizations;
        let connectedOrgIds = [];
        if (Array.isArray(rawConnectedOrgs)) {
            connectedOrgIds = rawConnectedOrgs;
        } else if (rawConnectedOrgs?.organizations) {
            connectedOrgIds = rawConnectedOrgs.organizations;
        }
        setSelectedOrganizationsForManagement(allOrganizations.filter(o =>
            connectedOrgIds.includes(o.id) || connectedOrgIds.includes(o.name)
        ));
        setManageOrganizationsOpen(true);
    };

    const handleSaveConnectedOrganizations = async () => {
        setIsSaving(true);
        try {
            const userDocRef = doc(db, 'users', userId);
            // Save as direct array to match OrganizationForm format (uses arrayUnion/arrayRemove)
            const orgIdsToSave = selectedOrganizationsForManagement.map(o => o.id);
            await updateDoc(userDocRef, {
                connectedOrganizations: orgIdsToSave,
                updatedAt: new Date()
            });
            setUser(prev => ({
                ...prev,
                connectedOrganizations: orgIdsToSave
            }));
            setOriginalUser(prev => ({
                ...prev,
                connectedOrganizations: orgIdsToSave
            }));
            enqueueSnackbar('Connected organizations updated!', { variant: 'success' });
            setManageOrganizationsOpen(false);
        } catch (err) {
            console.error("Error updating connected organizations:", err);
            enqueueSnackbar(`Error saving organizations: ${err.message}`, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordResetRequest = async () => {
        const validationError = getPasswordResetError(newPassword, confirmNewPassword);
        if (validationError) {
            setPasswordResetError(validationError);
            return;
        }
        setPasswordResetError('');
        setIsSaving(true);
        try {
            const resetPasswordFunction = httpsCallable(functions, 'adminResetUserPassword'); // Ensure this function exists
            await resetPasswordFunction({ uid: userId, newPassword: newPassword });
            enqueueSnackbar('Password reset successfully.', { variant: 'success' });
            setResetPasswordDialogOpen(false);
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (error) {
            console.error("Error resetting password:", error);
            setPasswordResetError(error.message || "Failed to reset password.");
            enqueueSnackbar(`Password reset failed: ${error.message}`, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };


    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
    }
    if (!user) {
        return <Box sx={{ p: 3 }}><Alert severity="warning">User data not available.</Alert></Box>;
    }

    const connectedCompanyIds = user.connectedCompanies?.companies || [];

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f8fafc'
        }}>
            {/* Header Section */}
            <Box sx={{
                p: 3,
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: 'white'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            color: '#111827',
                            fontSize: '20px',
                            mb: 0.5
                        }}>
                            {user ? `${user.firstName} ${user.lastName}` : 'User Details'}
                        </Typography>
                        <Typography sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                            mb: 2
                        }}>
                            Manage user information, permissions, and settings
                        </Typography>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '12px' }}>
                            <RouterLink to="/admin" sx={{
                                textDecoration: 'none',
                                color: '#6b7280',
                                fontSize: '12px',
                                '&:hover': { textDecoration: 'underline' }
                            }}>
                                Admin
                            </RouterLink>
                            <RouterLink to="/admin/users" sx={{
                                textDecoration: 'none',
                                color: '#6b7280',
                                fontSize: '12px',
                                '&:hover': { textDecoration: 'underline' }
                            }}>
                                Users
                            </RouterLink>
                            <Typography sx={{ color: '#374151', fontSize: '12px' }}>
                                {user ? `${user.firstName} ${user.lastName}` : 'Detail'}
                            </Typography>
                        </Breadcrumbs>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {editMode ? (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveChanges}
                                    startIcon={<SaveIcon />}
                                    disabled={isSaving}
                                    size="small"
                                    sx={{ fontSize: '12px' }}
                                >
                                    {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleEditToggle}
                                    startIcon={<CancelIcon />}
                                    size="small"
                                    sx={{ fontSize: '12px' }}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                {(() => {
                                    // Get the target user's role
                                    let targetRole = 'user';
                                    if (typeof user?.role === 'string') {
                                        targetRole = user.role;
                                    } else if (user?.role?.id) {
                                        targetRole = user.role.id;
                                    }

                                    // Check if emulation is possible
                                    const canEmulate = canEmulateRole(effectiveCurrentRole, targetRole);
                                    const hasCompany = (user?.connectedCompanies?.companies || []).length > 0;
                                    const isEmulationPossible = canEmulate && hasCompany;

                                    // Determine tooltip message
                                    let tooltipMessage = '';
                                    if (!canEmulate) {
                                        tooltipMessage = `Cannot emulate: ${targetRole} role is at or above your level`;
                                    } else if (!hasCompany) {
                                        tooltipMessage = 'Cannot emulate: user has no connected companies';
                                    }

                                    return (
                                        <Button
                                            variant="outlined"
                                            onClick={handleEmulateUser}
                                            startIcon={<PersonSearchIcon />}
                                            size="small"
                                            disabled={!isEmulationPossible}
                                            title={tooltipMessage}
                                            sx={{ fontSize: '12px' }}
                                        >
                                            Emulate User
                                        </Button>
                                    );
                                })()}
                                <Button
                                    variant="outlined"
                                    onClick={handleEditToggle}
                                    startIcon={<EditIcon />}
                                    size="small"
                                    sx={{ fontSize: '12px' }}
                                >
                                    Edit User
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 3
            }}>
                <Paper sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}>
                    <Grid container>
                        {/* Left Pane: Avatar and Core Info */}
                        <Grid item xs={12} md={4} sx={{
                            borderRight: { md: '1px solid #e5e7eb' },
                            p: 3
                        }}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Avatar sx={{
                                    width: 100,
                                    height: 100,
                                    margin: '0 auto 16px',
                                    fontSize: '2.5rem',
                                    backgroundColor: '#1f2937'
                                }}>
                                    {user.firstName?.[0]?.toUpperCase()}{user.lastName?.[0]?.toUpperCase()}
                                </Avatar>
                                <Typography sx={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    mb: 1
                                }}>
                                    {user.firstName || ''} {user.lastName || ''}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Chip
                                        label={user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
                                        color={
                                            user.status === 'active' ? 'success' :
                                                user.status === 'inactive' ? 'default' :
                                                    user.status === 'suspended' ? 'error' :
                                                        'warning'
                                        }
                                        size="small"
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600
                                        }}
                                    />
                                    <Chip
                                        label={getRoleDisplayName(user.role)}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            borderColor: getRoleColor(user.role),
                                            color: getRoleColor(user.role),
                                            backgroundColor: `${getRoleColor(user.role)}10`
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Stack spacing={2}>
                                <Box>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 0.5
                                    }}>
                                        Email
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                        {user.email || '—'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 0.5
                                    }}>
                                        Phone
                                    </Typography>
                                    {editMode ? (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                name="phone"
                                                placeholder="(555) 123-4567"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiInputBase-input': { fontSize: '12px' },
                                                    '& .MuiInputLabel-root': { fontSize: '12px' }
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                name="phoneExtension"
                                                placeholder="Ext"
                                                value={formData.phoneExtension}
                                                onChange={handleInputChange}
                                                sx={{
                                                    width: '80px',
                                                    '& .MuiInputBase-input': { fontSize: '12px' },
                                                    '& .MuiInputLabel-root': { fontSize: '12px' }
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                            {user.phone ?
                                                `${user.phone}${user.phoneExtension ? ` ext. ${user.phoneExtension}` : ''}`
                                                : '—'}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 0.5
                                    }}>
                                        Role
                                    </Typography>
                                    {editMode ? (
                                        <FormControl fullWidth size="small">
                                            <Select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                disabled={rolesLoading}
                                                sx={{
                                                    '& .MuiSelect-select': { fontSize: '12px' },
                                                    '& .MuiMenuItem-root': { fontSize: '12px' }
                                                }}
                                            >
                                                {getActiveRoles().map((role) => (
                                                    <MenuItem
                                                        key={role.roleId}
                                                        value={role.roleId}
                                                        sx={{
                                                            fontSize: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                backgroundColor: getRoleColor(role.roleId),
                                                                display: 'inline-block',
                                                                marginRight: 1
                                                            }}
                                                        />
                                                        {role.displayName}
                                                        {!role.isSystemRole && (
                                                            <Chip
                                                                label="Custom"
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                sx={{
                                                                    ml: 'auto',
                                                                    fontSize: '9px',
                                                                    height: '16px'
                                                                }}
                                                            />
                                                        )}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: getRoleColor(user.role),
                                                    display: 'inline-block'
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                                {getRoleDisplayName(user.role)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 0.5
                                    }}>
                                        Status
                                    </Typography>
                                    {editMode ? (
                                        <FormControl fullWidth size="small">
                                            <Select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                sx={{
                                                    '& .MuiSelect-select': { fontSize: '12px' },
                                                    '& .MuiMenuItem-root': { fontSize: '12px' }
                                                }}
                                            >
                                                <MenuItem value="active" sx={{ fontSize: '12px' }}>Active</MenuItem>
                                                <MenuItem value="inactive" sx={{ fontSize: '12px' }}>Inactive</MenuItem>
                                                <MenuItem value="suspended" sx={{ fontSize: '12px' }}>Suspended</MenuItem>
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                            {user.status || '—'}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>

                        {/* Right Pane: Tabs for Details */}
                        <Grid item xs={12} md={8}>
                            <Paper variant="outlined" sx={{ height: '100%' }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    aria-label="user details tabs"
                                    sx={{
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        '& .MuiTab-root': {
                                            fontSize: '12px',
                                            textTransform: 'none',
                                            minHeight: '48px'
                                        }
                                    }}
                                >
                                    <Tab label="Details" icon={<PersonIcon />} iconPosition="start" />
                                    <Tab label="Organizations" icon={<AccountTreeIcon />} iconPosition="start" />
                                    <Tab label="Companies" icon={<BusinessIcon />} iconPosition="start" />
                                    <Tab label="Security" icon={<AdminPanelSettingsIcon />} iconPosition="start" />
                                </Tabs>

                                {/* Tab Panel 1: Details */}
                                {activeTab === 0 && (
                                    <Box sx={{ p: 3 }}>
                                        <Typography sx={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: '#374151',
                                            mb: 3
                                        }}>
                                            User Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="First Name"
                                                    fullWidth
                                                    name="firstName"
                                                    value={editMode ? formData.firstName : user.firstName}
                                                    onChange={handleInputChange}
                                                    disabled={!editMode}
                                                    error={!!formErrors.firstName}
                                                    helperText={formErrors.firstName}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                                        '& .MuiInputLabel-root': { fontSize: '12px' },
                                                        '& .MuiFormHelperText-root': { fontSize: '11px' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Last Name"
                                                    fullWidth
                                                    name="lastName"
                                                    value={editMode ? formData.lastName : user.lastName}
                                                    onChange={handleInputChange}
                                                    disabled={!editMode}
                                                    error={!!formErrors.lastName}
                                                    helperText={formErrors.lastName}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                                        '& .MuiInputLabel-root': { fontSize: '12px' },
                                                        '& .MuiFormHelperText-root': { fontSize: '11px' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Email"
                                                    fullWidth
                                                    value={user.email || '—'}
                                                    disabled
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                                        '& .MuiInputLabel-root': { fontSize: '12px' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Phone"
                                                    fullWidth
                                                    name="phone"
                                                    value={editMode ? formData.phone : (user.phone || '—')}
                                                    onChange={handleInputChange}
                                                    disabled={!editMode}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                                        '& .MuiInputLabel-root': { fontSize: '12px' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#374151', mb: 0.5 }}>
                                                    Joined:
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                                    {formatDate(user.createdAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#374151', mb: 0.5 }}>
                                                    Last Updated:
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                                    {formatDate(user.updatedAt)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Tab Panel 2: Connected Organizations */}
                                {activeTab === 1 && (
                                    <Box sx={{ p: 3 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                            <Typography sx={{
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: '#374151',
                                            }}>
                                                Connected Organizations
                                            </Typography>
                                            <Button
                                                startIcon={<EditIcon />}
                                                onClick={handleManageOrganizations}
                                                size="small"
                                                sx={{ fontSize: '12px' }}
                                            >
                                                Manage
                                            </Button>
                                        </Stack>
                                        <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
                                            Connecting a user to an organization lets them see and manage the organization's record; it grants no access to its companies' data. Data access across an organization comes from an Organization access grant (below).
                                        </Alert>
                                        {(() => {
                                            // Handle both array format and object format
                                            const rawConnectedOrgs = user?.connectedOrganizations;
                                            let connectedOrgIds = [];
                                            if (Array.isArray(rawConnectedOrgs)) {
                                                connectedOrgIds = rawConnectedOrgs;
                                            } else if (rawConnectedOrgs?.organizations) {
                                                connectedOrgIds = rawConnectedOrgs.organizations;
                                            }
                                            return connectedOrgIds.length > 0 ? (
                                                <List dense>
                                                    {connectedOrgIds.map(orgId => (
                                                        <ListItem key={orgId} disableGutters>
                                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                                <AccountTreeIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography sx={{ fontSize: '12px', color: '#111827', fontWeight: 500 }}>
                                                                        {organizationMap[orgId] || orgId}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                                                        Organization ID: {orgId}
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                                                    No organizations connected. Click "Manage" to add organizations.
                                                </Typography>
                                            );
                                        })()}

                                        <OrganizationAccessSection
                                            uid={userId}
                                            user={user}
                                            organizations={allOrganizations}
                                            organizationMap={organizationMap}
                                            companyMap={companyMap}
                                            canEdit={['admin', 'superadmin'].includes(effectiveCurrentRole)}
                                            onSaved={(orgAccess) => {
                                                // Server-written; mirror it locally so the page shows the result.
                                                setUser(prev => ({ ...prev, orgAccess: { ...(prev?.orgAccess || {}), ...orgAccess } }));
                                                setOriginalUser(prev => ({ ...prev, orgAccess: { ...(prev?.orgAccess || {}), ...orgAccess } }));
                                                enqueueSnackbar('Organization access updated.', { variant: 'success' });
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Tab Panel 3: Connected Companies */}
                                {activeTab === 2 && (
                                    <Box sx={{ p: 3 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                            <Typography sx={{
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: '#374151'
                                            }}>
                                                Connected Companies
                                            </Typography>
                                            <Button
                                                startIcon={<EditIcon />}
                                                onClick={handleManageCompanies}
                                                size="small"
                                                sx={{ fontSize: '12px' }}
                                            >
                                                Manage
                                            </Button>
                                        </Stack>
                                        {connectedCompanyIds.length > 0 ? (
                                            <List dense>
                                                {connectedCompanyIds.map(companyId => (
                                                    <ListItem key={companyId} disableGutters>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <BusinessIcon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography sx={{ fontSize: '12px', color: '#111827', fontWeight: 500 }}>
                                                                    {companyMap[companyId] || companyId}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                                                    Company ID: {companyId}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                No companies connected.
                                            </Typography>
                                        )}

                                        {/* Connected Customers Section - optional customer restriction (hidden for superadmin/admin) */}
                                        {supportsCustomerRestriction(user) && (
                                            <>
                                                <Divider sx={{ my: 3 }} />
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                                    <Box>
                                                        <Typography sx={{
                                                            fontSize: '16px',
                                                            fontWeight: 600,
                                                            color: '#374151'
                                                        }}>
                                                            Connected Customers
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '11px', color: '#6b7280', mt: 0.5 }}>
                                                            Restrict this user to specific customers (optional). Empty = full access to all customers in the connected companies.
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        startIcon={<EditIcon />}
                                                        onClick={handleManageCustomers}
                                                        size="small"
                                                        sx={{ fontSize: '12px' }}
                                                        disabled={connectedCompanyIds.length === 0}
                                                    >
                                                        Manage
                                                    </Button>
                                                </Stack>

                                                {loadingCustomers ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={16} />
                                                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                            Loading customers...
                                                        </Typography>
                                                    </Box>
                                                ) : connectedCompanyIds.length === 0 ? (
                                                    <Alert severity="info" sx={{ fontSize: '12px' }}>
                                                        Connect companies first to assign customers.
                                                    </Alert>
                                                ) : (user?.connectedCustomers?.customers || []).length > 0 ? (
                                                    <List dense>
                                                        {/* Stored array holds both doc ids and customerID codes; render one
                                                            row per resolvable customer (keyed by doc id) to avoid duplicates. */}
                                                        {(user?.connectedCustomers?.customers || [])
                                                            .filter(customerId => customerMap[customerId])
                                                            .map(customerId => {
                                                                const customer = customerMap[customerId];
                                                                return (
                                                                    <ListItem key={customerId} disableGutters>
                                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                                            <PeopleIcon fontSize="small" sx={{ color: '#00bcd4' }} />
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Typography sx={{ fontSize: '12px', color: '#111827', fontWeight: 500 }}>
                                                                                    {customer?.name || customerId}
                                                                                </Typography>
                                                                            }
                                                                            secondary={
                                                                                <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                                                                    {customer?.companyName || 'Unknown Company'} • ID: {customer?.customerId || customerId}
                                                                                </Typography>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                );
                                                            })}
                                                    </List>
                                                ) : (
                                                    <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                        No customers connected. This user will have access to all customers in connected companies.
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                )}

                                {/* Tab Panel 4: Account Security */}
                                {activeTab === 3 && (
                                    <Box sx={{ p: 3 }}>
                                        <Typography sx={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: '#374151',
                                            mb: 3
                                        }}>
                                            Account Security
                                        </Typography>
                                        <Stack spacing={3}>
                                            <Box>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#374151', mb: 0.5 }}>
                                                    Last Login:
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#111827' }}>
                                                    {formatDate(user.lastLogin) || 'Never'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#374151', mb: 1 }}>
                                                    Password Management:
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<LockResetIcon />}
                                                    onClick={() => setResetPasswordDialogOpen(true)}
                                                    size="small"
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    Reset User Password
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Manage Connected Companies Dialog */}
                <Dialog open={manageCompaniesOpen} onClose={() => setManageCompaniesOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                        Manage Connected Companies
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 2 }}>
                            Currently Connected:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{
                            mb: 2,
                            p: 1,
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            minHeight: '40px',
                            backgroundColor: '#f8fafc'
                        }}>
                            {selectedCompaniesForManagement.length > 0 ? (
                                selectedCompaniesForManagement.map((company) => (
                                    <Chip
                                        key={company.id}
                                        label={`${company.name} (${company.id})`}
                                        onDelete={() => {
                                            setSelectedCompaniesForManagement(prev => prev.filter(c => c.id !== company.id));
                                        }}
                                        color="primary"
                                        size="small"
                                        sx={{ fontSize: '11px' }}
                                    />
                                ))
                            ) : (
                                <Typography sx={{ fontSize: '12px', color: '#6b7280', p: 1 }}>
                                    No companies currently connected.
                                </Typography>
                            )}
                        </Stack>

                        <Autocomplete
                            multiple
                            id="add-companies-autocomplete"
                            options={allCompanies.filter(opt =>
                                !selectedCompaniesForManagement.find(sel => sel.id === opt.id)
                            )}
                            getOptionLabel={(option) => `${option.name} (${option.id})`}
                            value={[]}
                            onChange={(event, newValue) => {
                                setSelectedCompaniesForManagement(prev => {
                                    const newSelections = newValue.filter(nv => !prev.find(p => p.id === nv.id));
                                    return [...prev, ...newSelections];
                                });
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#111827' }}>
                                        {option.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                        Company ID: {option.id}
                                    </Typography>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Add Companies"
                                    placeholder="Search and select companies to add"
                                    size="small"
                                    sx={{
                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                        '& .MuiInputLabel-root': { fontSize: '12px' }
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            sx={{ mt: 2 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setManageCompaniesOpen(false)}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveConnectedCompanies}
                            variant="contained"
                            disabled={isSaving}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Manage Connected Customers Dialog */}
                <Dialog open={manageCustomersOpen} onClose={() => setManageCustomersOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                        Manage Connected Customers
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280', mb: 2 }}>
                            Select which customers this user can access. If no customers are selected, the user will have access to all customers in their connected companies.
                        </Typography>

                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 2 }}>
                            Currently Connected:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{
                            mb: 2,
                            p: 1,
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            minHeight: '40px',
                            backgroundColor: '#f8fafc'
                        }}>
                            {selectedCustomersForManagement.length > 0 ? (
                                selectedCustomersForManagement.map((customer) => (
                                    <Chip
                                        key={customer.id}
                                        label={`${customer.name} (${customer.companyName})`}
                                        onDelete={() => {
                                            setSelectedCustomersForManagement(prev => prev.filter(c => c.id !== customer.id));
                                        }}
                                        sx={{
                                            fontSize: '11px',
                                            backgroundColor: '#00bcd4',
                                            color: 'white',
                                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' }
                                        }}
                                        size="small"
                                    />
                                ))
                            ) : (
                                <Typography sx={{ fontSize: '12px', color: '#6b7280', p: 1 }}>
                                    No customers selected - user will have access to all customers in connected companies.
                                </Typography>
                            )}
                        </Stack>

                        <Autocomplete
                            multiple
                            id="add-customers-autocomplete"
                            options={allCustomers.filter(opt =>
                                !selectedCustomersForManagement.find(sel => sel.id === opt.id)
                            )}
                            getOptionLabel={(option) => `${option.name} (${option.companyName})`}
                            value={[]}
                            onChange={(event, newValue) => {
                                setSelectedCustomersForManagement(prev => {
                                    const newSelections = newValue.filter(nv => !prev.find(p => p.id === nv.id));
                                    return [...prev, ...newSelections];
                                });
                            }}
                            groupBy={(option) => option.companyName}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#111827' }}>
                                        {option.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                        Customer ID: {option.customerId}
                                    </Typography>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Add Customers"
                                    placeholder="Search and select customers to add"
                                    size="small"
                                    sx={{
                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                        '& .MuiInputLabel-root': { fontSize: '12px' }
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            sx={{ mt: 2 }}
                            loading={loadingCustomers}
                            loadingText="Loading customers..."
                            noOptionsText={allCustomers.length === 0 ? "No customers found in connected companies" : "No more customers available"}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setManageCustomersOpen(false)}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveConnectedCustomers}
                            variant="contained"
                            disabled={isSaving}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Manage Connected Organizations Dialog */}
                <Dialog open={manageOrganizationsOpen} onClose={() => setManageOrganizationsOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                        Manage Connected Organizations
                    </DialogTitle>
                    <DialogContent dividers>
                        <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
                            Connecting a user to an organization grants no access to its companies' data. Use Organization access on the Organizations tab for a view or member grant.
                        </Alert>

                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', mb: 2 }}>
                            Currently Connected:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{
                            mb: 2,
                            p: 1,
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            minHeight: '40px',
                            backgroundColor: '#f8fafc'
                        }}>
                            {selectedOrganizationsForManagement.length > 0 ? (
                                selectedOrganizationsForManagement.map((org) => (
                                    <Chip
                                        key={org.id}
                                        avatar={org.logo ? (
                                            <Avatar src={org.logo} sx={{ width: 20, height: 20 }} />
                                        ) : (
                                            <Avatar sx={{ width: 20, height: 20, bgcolor: '#6366f1' }}>
                                                <AccountTreeIcon sx={{ fontSize: 12 }} />
                                            </Avatar>
                                        )}
                                        label={`${org.name} (${org.id})`}
                                        onDelete={() => {
                                            setSelectedOrganizationsForManagement(prev => prev.filter(o => o.id !== org.id));
                                        }}
                                        sx={{
                                            fontSize: '11px',
                                            backgroundColor: '#6366f1',
                                            color: 'white',
                                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' }
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography sx={{ fontSize: '12px', color: '#6b7280', p: 1 }}>
                                    No organizations currently connected.
                                </Typography>
                            )}
                        </Stack>

                        <Autocomplete
                            multiple
                            id="add-organizations-autocomplete"
                            options={allOrganizations.filter(opt =>
                                !selectedOrganizationsForManagement.find(sel => sel.id === opt.id)
                            )}
                            getOptionLabel={(option) => `${option.name} (${option.id})`}
                            value={[]}
                            onChange={(event, newValue) => {
                                setSelectedOrganizationsForManagement(prev => {
                                    const newSelections = newValue.filter(nv => !prev.find(p => p.id === nv.id));
                                    return [...prev, ...newSelections];
                                });
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.logo ? (
                                        <Avatar src={option.logo} sx={{ width: 24, height: 24 }} />
                                    ) : (
                                        <Avatar sx={{ width: 24, height: 24, bgcolor: '#6366f1' }}>
                                            <AccountTreeIcon sx={{ fontSize: 14 }} />
                                        </Avatar>
                                    )}
                                    <Box>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>{option.id}</Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Add Organizations"
                                    placeholder="Search and select organizations..."
                                    size="small"
                                    sx={{
                                        '& .MuiInputBase-input': { fontSize: '12px' },
                                        '& .MuiInputLabel-root': { fontSize: '12px' }
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            sx={{ mt: 2 }}
                            noOptionsText={allOrganizations.length === 0 ? "No organizations found" : "No more organizations available"}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setManageOrganizationsOpen(false)}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveConnectedOrganizations}
                            variant="contained"
                            disabled={isSaving}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Reset Password Dialog */}
                <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                        Reset User Password
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2, fontSize: '12px', color: '#6b7280' }}>
                            Enter a new password for {user.email}.
                        </DialogContentText>
                        {passwordResetError && (
                            <Alert severity="error" sx={{ mb: 2, fontSize: '12px' }}>
                                {passwordResetError}
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            margin="dense"
                            label="New Password"
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input': { fontSize: '12px' },
                                '& .MuiInputLabel-root': { fontSize: '12px' }
                            }}
                        />
                        <TextField
                            margin="dense"
                            label="Confirm New Password"
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            size="small"
                            sx={{
                                '& .MuiInputBase-input': { fontSize: '12px' },
                                '& .MuiInputLabel-root': { fontSize: '12px' }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setResetPasswordDialogOpen(false);
                                setPasswordResetError('');
                            }}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePasswordResetRequest}
                            variant="contained"
                            disabled={isSaving}
                            size="small"
                            sx={{ fontSize: '12px' }}
                        >
                            {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Reset Password'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default UserDetail; 